import { useState, useMemo } from 'react';

import { useLoansStore, useRevenueStore } from '@/hooks/useSupabaseStore';
import { useCapital } from '@/hooks/useCapital';

interface UnifiedActivity {
  id: string;
  type: 'loan_created' | 'loan_redeemed' | 'loan_extended' | 'loan_status_change' | 'loan_field_change' | 'capital_deposit' | 'capital_withdraw';
  title: string;
  description: string;
  amount?: number;
  date: string;
  icon: string;
  color: string;
  meta?: string;
}

function timeAgo(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Vừa xong';
  if (diffMin < 60) return `${diffMin} phút trước`;
  if (diffHour < 24) return `${diffHour} giờ trước`;
  if (diffDay < 7) return `${diffDay} ngày trước`;
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function getDateKey(dateStr: string) {
  return dateStr.slice(0, 10);
}

export default function ActivityPage() {
  const { loans, loading: loansLoading } = useLoansStore();
  const { logs: revenueLogs, loading: revLoading } = useRevenueStore();

  const { capitalLogs } = useCapital();
  const [filterType, setFilterType] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [expandedDate, setExpandedDate] = useState<string | null>(null);



  // Build unified activities
  const activities = useMemo<UnifiedActivity[]>(() => {
    const list: UnifiedActivity[] = [];

    // 1. Loan created
    loans.forEach(loan => {
      list.push({
        id: `loan-create-${loan.id}`,
        type: 'loan_created',
        title: 'Tạo khoản vay mới',
        description: `${loan.customerName} — ${loan.assetName} (${loan.id})`,
        amount: loan.loanAmount,
        date: loan.startDate,
        icon: 'ri-file-add-line',
        color: 'bg-blue-100 text-blue-600',
        meta: loan.assetCategory,
      });
    });

    // 2. Revenue logs (redeem & extend)
    revenueLogs.forEach(log => {
      if (log.type === 'redeem') {
        list.push({
          id: `rev-redeem-${log.id}`,
          type: 'loan_redeemed',
          title: 'Chuộc đồ',
          description: `${log.customerName} · ${log.assetName}`,
          amount: log.interestAmount,
          date: log.createdAt,
          icon: 'ri-checkbox-circle-line',
          color: 'bg-emerald-100 text-emerald-600',
          meta: log.loanId,
        });
      } else if (log.type === 'extend') {
        list.push({
          id: `rev-extend-${log.id}`,
          type: 'loan_extended',
          title: 'Gia hạn / Đóng lãi',
          description: `${log.customerName} · ${log.assetName}`,
          amount: log.interestAmount,
          date: log.createdAt,
          icon: 'ri-time-line',
          color: 'bg-teal-100 text-teal-600',
          meta: log.note ? log.note.slice(0, 60) : `+${log.extendDays} ngày`,
        });
      }
    });

    // 3. Capital transactions
    capitalLogs.forEach(log => {
      list.push({
        id: `cap-${log.id}`,
        type: log.type === 'deposit' ? 'capital_deposit' : 'capital_withdraw',
        title: log.type === 'deposit' ? 'Nhập thêm vốn' : 'Rút vốn',
        description: log.note || (log.type === 'deposit' ? 'Bổ sung quỹ vốn kinh doanh' : 'Rút quỹ vốn kinh doanh'),
        amount: log.amount,
        date: log.createdAt,
        icon: log.type === 'deposit' ? 'ri-add-circle-line' : 'ri-indeterminate-circle-line',
        color: log.type === 'deposit' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-500',
      });
    });

    // 4. Loan status changes from history
    // Since we can't easily get all histories, we'll derive from revenue logs + loan dates
    // But for field changes we'll use the loan_history table if we can fetch it
    // For now, skip bulk history since we'd need a backend endpoint

    return list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [loans, revenueLogs, capitalLogs]);

  const filtered = useMemo(() => {
    let result = activities;

    if (filterType !== 'all') {
      const map: Record<string, UnifiedActivity['type'][]> = {
        loan: ['loan_created', 'loan_redeemed', 'loan_extended', 'loan_status_change', 'loan_field_change'],
        revenue: ['loan_redeemed', 'loan_extended'],
        capital: ['capital_deposit', 'capital_withdraw'],
      };
      const types = map[filterType];
      if (types) result = result.filter(a => types.includes(a.type));
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(a =>
        a.title.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q) ||
        (a.meta && a.meta.toLowerCase().includes(q))
      );
    }

    return result;
  }, [activities, filterType, search]);

  // Group by date
  const grouped = useMemo(() => {
    const map: Record<string, UnifiedActivity[]> = {};
    filtered.forEach(a => {
      const key = getDateKey(a.date);
      if (!map[key]) map[key] = [];
      map[key].push(a);
    });
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filtered]);

  const todayKey = new Date().toISOString().slice(0, 10);
  const yesterdayKey = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

  function formatGroupLabel(key: string): string {
    if (key === todayKey) return 'Hôm nay';
    if (key === yesterdayKey) return 'Hôm qua';
    const d = new Date(key + 'T00:00:00');
    return d.toLocaleDateString('vi-VN', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  const loading = loansLoading || revLoading;

  return (<>
      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 md:mb-5">
        <div className="bg-white rounded-xl p-4">
          <p className="text-gray-400 text-xs mb-1">Tổng hoạt động</p>
          <p className="font-bold text-xl text-gray-800">{activities.length}</p>
          <p className="text-xs text-gray-400 mt-0.5">Tất cả thời gian</p>
        </div>
        <div className="bg-white rounded-xl p-4">
          <p className="text-gray-400 text-xs mb-1">Khoản vay</p>
          <p className="font-bold text-xl text-blue-600">{activities.filter(a => ['loan_created', 'loan_redeemed', 'loan_extended'].includes(a.type)).length}</p>
          <p className="text-xs text-gray-400 mt-0.5">Tạo / Chuộc / Gia hạn</p>
        </div>
        <div className="bg-white rounded-xl p-4">
          <p className="text-gray-400 text-xs mb-1">Doanh thu</p>
          <p className="font-bold text-xl text-teal-600">{activities.filter(a => ['loan_redeemed', 'loan_extended'].includes(a.type)).length}</p>
          <p className="text-xs text-gray-400 mt-0.5">Giao dịch thu tiền</p>
        </div>
        <div className="bg-white rounded-xl p-4">
          <p className="text-gray-400 text-xs mb-1">Biến động vốn</p>
          <p className="font-bold text-xl text-emerald-600">{activities.filter(a => ['capital_deposit', 'capital_withdraw'].includes(a.type)).length}</p>
          <p className="text-xs text-gray-400 mt-0.5">Nhập / Rút vốn</p>
        </div>
      </div>

      {/* Filter + Search */}
      <div className="bg-white rounded-2xl px-4 py-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          {[
            { value: 'all', label: 'Tất cả', icon: 'ri-apps-line' },
            { value: 'loan', label: 'Khoản vay', icon: 'ri-file-list-3-line' },
            { value: 'revenue', label: 'Doanh thu', icon: 'ri-money-dollar-circle-line' },
            { value: 'capital', label: 'Vốn', icon: 'ri-safe-2-line' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setFilterType(opt.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                filterType === opt.value ? 'bg-[#f59e0b] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className={`${opt.icon} text-sm`}></i></span>
              {opt.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px] ml-auto">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-search-line text-sm"></i>
          </span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Tìm theo tên, mã, tài sản..."
            className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]"
          />
        </div>
        {search && (
          <button
            onClick={() => setSearch('')}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-close-line text-sm"></i>
            Xóa
          </button>
        )}
      </div>

      {/* Activity list */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center text-gray-400">
          <i className="ri-loader-4-line text-3xl animate-spin block mb-2"></i>
          <p className="text-sm">Đang tải dữ liệu...</p>
        </div>
      ) : grouped.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-gray-400">
          <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
            <i className="ri-history-line text-4xl"></i>
          </span>
          <p className="text-sm font-medium">Không tìm thấy hoạt động nào</p>
          <p className="text-xs mt-1">Thử đổi bộ lọc hoặc tìm kiếm khác</p>
        </div>
      ) : (
        <div className="space-y-3">
          {grouped.map(([dateKey, items]) => {
            const isExpanded = expandedDate === dateKey;
            const displayItems = isExpanded ? items : items.slice(0, 8);
            const hasMore = items.length > 8;
            return (
              <div key={dateKey} className="bg-white rounded-2xl overflow-hidden">
                {/* Date header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50/60">
                  <div className="flex items-center gap-2.5">
                    <span className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-lg">
                      <i className="ri-calendar-check-line text-amber-600 text-sm"></i>
                    </span>
                    <span className="text-sm font-bold text-gray-800">{formatGroupLabel(dateKey)}</span>
                    <span className="text-xs text-gray-400">{items.length} hoạt động</span>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">
                      {items.reduce((s, a) => s + (a.amount || 0), 0).toLocaleString('vi-VN')}đ
                    </p>
                  </div>
                </div>

                {/* Items */}
                <div className="divide-y divide-gray-50">
                  {displayItems.map(activity => (
                    <div key={activity.id} className="flex items-start gap-3 px-5 py-3.5 hover:bg-gray-50/50 transition-colors">
                      <div className={`w-9 h-9 flex items-center justify-center rounded-xl shrink-0 ${activity.color}`}>
                        <i className={`${activity.icon} text-base`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-gray-800">{activity.title}</p>
                          {activity.meta && (
                            <span className="text-[10px] font-medium bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full whitespace-nowrap">
                              {activity.meta}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{activity.description}</p>
                        {activity.amount !== undefined && activity.amount > 0 && (
                          <p className="text-xs font-semibold text-amber-600 mt-0.5">
                            {activity.amount.toLocaleString('vi-VN')}đ
                          </p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[11px] text-gray-400 whitespace-nowrap">{timeAgo(activity.date)}</p>
                        <p className="text-[10px] text-gray-300 mt-0.5">
                          {new Date(activity.date).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Show more */}
                {hasMore && (
                  <button
                    onClick={() => setExpandedDate(isExpanded ? null : dateKey)}
                    className="w-full py-2.5 text-xs font-medium text-[#f59e0b] hover:bg-amber-50 transition-colors cursor-pointer border-t border-gray-50"
                  >
                    {isExpanded ? (
                      <span className="flex items-center justify-center gap-1">
                        <i className="ri-arrow-up-s-line text-sm"></i>
                        Thu gọn
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-1">
                        <i className="ri-arrow-down-s-line text-sm"></i>
                        Xem thêm {items.length - 8} hoạt động
                      </span>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}